import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  AppData,
  Fach,
  GekoppelteKlasse,
  Klasse,
  Lehrperson,
  TabName,
  Zuteilung,
} from "../types";
import { loadFromStorage, saveToStorage, generateId } from "../utils/storage";
import { calculateEntschaedigung } from "../utils/calculations";
import { getDefaultFaecher, getDemoData } from "../utils/demoData";

interface DataContextType {
  data: AppData;
  activeTab: TabName;
  setActiveTab: (tab: TabName) => void;
  selectedFachId: string | null;
  navigateToFachDetail: (fachId: string) => void;
  addLehrperson: (lp: Omit<Lehrperson, "id">) => void;
  deleteLehrperson: (id: string) => void;
  updateLehrperson: (id: string, updates: Partial<Lehrperson>) => void;
  addKlasse: (kl: Omit<Klasse, "id">) => void;
  deleteKlasse: (id: string) => void;
  updateKlasse: (id: string, updates: Partial<Klasse>) => void;
  addFach: (fach: Omit<Fach, "id">) => void;
  updateFach: (id: string, updates: Partial<Omit<Fach, "id">>) => void;
  deleteFach: (id: string) => void;
  addZuteilung: (
    zt: Omit<Zuteilung, "id" | "entschaedigungFaktor" | "entschaedigungStunden">
  ) => void;
  deleteZuteilung: (id: string) => void;
  updateZuteilung: (
    id: string,
    updates: Partial<
      Omit<Zuteilung, "id" | "entschaedigungFaktor" | "entschaedigungStunden">
    >
  ) => void;
  addGekoppelt: (gk: Omit<GekoppelteKlasse, "id">) => void;
  deleteGekoppelt: (id: string) => void;
  importData: (data: AppData) => void;
  loadDemoData: () => void;
  loadDefaultFaecher: () => void;
  clearAllData: () => void;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider = ({ children }: { children: ReactNode }) => {
  const [data, setData] = useState<AppData>(loadFromStorage());
  const [activeTab, setActiveTab] = useState<TabName>("dashboard");
  const [selectedFachId, setSelectedFachId] = useState<string | null>(null);

  const navigateToFachDetail = (fachId: string) => {
    setSelectedFachId(fachId);
    setActiveTab("fachDetail");
  };

  useEffect(() => {
    saveToStorage(data);
  }, [data]);

  const addLehrperson = (lp: Omit<Lehrperson, "id">) => {
    const newLp: Lehrperson = { ...lp, id: generateId("LP") };
    setData((prev) => ({
      ...prev,
      lehrpersonen: [...prev.lehrpersonen, newLp],
    }));
  };

  const deleteLehrperson = (id: string) => {
    setData((prev) => ({
      ...prev,
      lehrpersonen: prev.lehrpersonen.filter((l) => l.id !== id),
    }));
  };

  const updateLehrperson = (id: string, updates: Partial<Lehrperson>) => {
    setData((prev) => ({
      ...prev,
      lehrpersonen: prev.lehrpersonen.map((lp) =>
        lp.id === id ? { ...lp, ...updates } : lp
      ),
    }));
  };

  const addKlasse = (kl: Omit<Klasse, "id">) => {
    const newKl: Klasse = { ...kl, id: generateId("KL") };
    setData((prev) => ({ ...prev, klassen: [...prev.klassen, newKl] }));
  };

  const deleteKlasse = (id: string) => {
    setData((prev) => ({
      ...prev,
      klassen: prev.klassen.filter((k) => k.id !== id),
    }));
  };

  const updateKlasse = (id: string, updates: Partial<Klasse>) => {
    setData((prev) => ({
      ...prev,
      klassen: prev.klassen.map((kl) =>
        kl.id === id ? { ...kl, ...updates } : kl
      ),
    }));
  };

  const addFach = (fach: Omit<Fach, "id">) => {
    const newFach: Fach = { ...fach, id: generateId("F") };
    setData((prev) => ({ ...prev, faecher: [...prev.faecher, newFach] }));
  };

  const updateFach = (id: string, updates: Partial<Omit<Fach, "id">>) => {
    setData((prev) => ({
      ...prev,
      faecher: prev.faecher.map((f) => (f.id === id ? { ...f, ...updates } : f)),
    }));
  };

  const deleteFach = (id: string) => {
    setData((prev) => ({
      ...prev,
      faecher: prev.faecher.filter((f) => f.id !== id),
    }));
  };

  const addZuteilung = (
    zt: Omit<Zuteilung, "id" | "entschaedigungFaktor" | "entschaedigungStunden">
  ) => {
    const entsch = calculateEntschaedigung(
      zt.klasseId,
      zt.typ,
      zt.lektionen,
      data.klassen
    );
    const newZt: Zuteilung = {
      ...zt,
      id: generateId("ZT"),
      entschaedigungFaktor: entsch.faktor,
      entschaedigungStunden: entsch.stunden,
    };
    setData((prev) => ({ ...prev, zuteilungen: [...prev.zuteilungen, newZt] }));
  };

  const deleteZuteilung = (id: string) => {
    setData((prev) => ({
      ...prev,
      zuteilungen: prev.zuteilungen.filter((z) => z.id !== id),
    }));
  };

  const updateZuteilung = (
    id: string,
    updates: Partial<
      Omit<Zuteilung, "id" | "entschaedigungFaktor" | "entschaedigungStunden">
    >
  ) => {
    setData((prev) => {
      const zt = prev.zuteilungen.find((z) => z.id === id);
      if (!zt) return prev;

      const updatedZt = { ...zt, ...updates };
      const entsch = calculateEntschaedigung(
        updatedZt.klasseId,
        updatedZt.typ,
        updatedZt.lektionen,
        prev.klassen
      );

      return {
        ...prev,
        zuteilungen: prev.zuteilungen.map((z) =>
          z.id === id
            ? {
                ...updatedZt,
                entschaedigungFaktor: entsch.faktor,
                entschaedigungStunden: entsch.stunden,
              }
            : z
        ),
      };
    });
  };

  const addGekoppelt = (gk: Omit<GekoppelteKlasse, "id">) => {
    const newGk: GekoppelteKlasse = { ...gk, id: generateId("GK") };
    setData((prev) => ({ ...prev, gekoppelt: [...prev.gekoppelt, newGk] }));
  };

  const deleteGekoppelt = (id: string) => {
    setData((prev) => ({
      ...prev,
      gekoppelt: prev.gekoppelt.filter((g) => g.id !== id),
    }));
  };

  const importData = (newData: AppData) => {
    setData(newData);
  };

  const loadDemoData = () => {
    setData(getDemoData());
  };

  const loadDefaultFaecher = () => {
    const defaultFaecher = getDefaultFaecher();
    const newFaecher = defaultFaecher.map((fach) => ({
      ...fach,
      id: generateId("F"),
    }));
    setData((prev) => ({ ...prev, faecher: [...prev.faecher, ...newFaecher] }));
  };

  const clearAllData = () => {
    setData({
      lehrpersonen: [],
      klassen: [],
      faecher: [],
      zuteilungen: [],
      gekoppelt: [],
    });
  };

  return (
    <DataContext.Provider
      value={{
        data,
        activeTab,
        setActiveTab,
        selectedFachId,
        navigateToFachDetail,
        addLehrperson,
        deleteLehrperson,
        updateLehrperson,
        addKlasse,
        deleteKlasse,
        updateKlasse,
        addFach,
        updateFach,
        deleteFach,
        addZuteilung,
        deleteZuteilung,
        updateZuteilung,
        addGekoppelt,
        deleteGekoppelt,
        importData,
        loadDemoData,
        loadDefaultFaecher,
        clearAllData,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};
